import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField,
  MenuItem, Snackbar, Alert, Grid, Card, CardContent, IconButton, Tooltip, Stack, Chip,
  CircularProgress, Switch, FormControlLabel, Divider, InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GavelIcon from '@mui/icons-material/Gavel';
import UndoIcon from '@mui/icons-material/Undo';
import { getAllUsers } from '../../services/authService';
import {
  getPenalties, getPenaltyContext, getPenaltyMonths, createPenalty, updatePenalty,
  revokePenalty, deletePenalty,
} from '../../services/penaltyService';

const fmtVnd = (n) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('vi-VN').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';
const toInputDate = (d) => new Date(d).toISOString().slice(0, 10);

const PENALTY_TYPES = [
  { value: 'fake_order', label: 'Buff đơn ảo (nhờ người quen / nick phụ đặt rồi huỷ)' },
  { value: 'self_buy', label: 'Tự đặt đơn trên phiên của mình' },
  { value: 'mass_cancel', label: 'Đơn huỷ / hoàn hàng loạt sau khi chốt GMV' },
  { value: 'inflated_report', label: 'Khai GMV cao hơn số thật trên Seller Center' },
  { value: 'fake_evidence', label: 'Ảnh bằng chứng chỉnh sửa / lấy của phiên khác' },
  { value: 'other', label: 'Khác' },
];
const TYPE_SHORT = {
  fake_order: 'Đơn ảo', self_buy: 'Tự đặt đơn', mass_cancel: 'Huỷ hàng loạt',
  inflated_report: 'Khai khống', fake_evidence: 'Ảnh giả', other: 'Khác',
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const emptyForm = () => ({
  userId: '',
  // Ngày quyết định → quyết định KỲ LƯƠNG chịu phạt. Mặc định hôm nay: tháng vi phạm thường đã trả lương.
  periodDate: toInputDate(new Date()),
  violationMonths: [],
  installments: 1,
  type: 'fake_order', description: '',
  fakeGMV: '', voidSession: false, revenueIds: [], adjustRevenue: true, clawbackFakeGmvBenefit: true,
  gmvPenaltyPercent: '', fixedAmount: '',
  cutGmvBonus: false, cutAttendanceBonus: false, cutNightAllowance: false,
});

const monthLabel = (mk) => `T${Number(mk.slice(5))}/${mk.slice(0, 4)}`;

// Khớp pickGmvTier + gmvTierAmount ở backend/utils/livePolicyEngine.js — hai bên phải ra cùng số
const gmvTierAmount = (t, gmv) => {
  let a = t.bonus || 0;
  if (t.stepGMV > 0 && t.stepBonus > 0) {
    const steps = Math.floor((gmv - t.minGMV) / t.stepGMV);
    if (steps > 0) a += steps * t.stepBonus;
  }
  return a;
};
const pickGmvTier = (tiers, gmv) => (tiers || []).reduce((best, t) =>
  (gmv >= t.minGMV && (t.maxGMV == null || gmv <= t.maxGMV))
    ? Math.max(best, gmvTierAmount(t, gmv)) : best, 0);

const PenaltyManagement = ({ isMobile }) => {
  const [users, setUsers] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [loading, setLoading] = useState(false);

  const [monthKey, setMonthKey] = useState(currentMonthKey());
  const [filterUserId, setFilterUserId] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [availableMonths, setAvailableMonths] = useState([]);
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeNote, setRevokeNote] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [noti, setNoti] = useState({ open: false, message: '', severity: 'success' });

  const show = (message, severity = 'success') => setNoti({ open: true, message, severity });
  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const liveUsers = useMemo(
    () => users.filter(u => ['live_shopee', 'live_tiktok', 'live_multi', 'leader_live'].includes(u.role)),
    [users]
  );

  const fetchUsers = useCallback(async () => {
    const res = await getAllUsers();
    if (res.success) setUsers(res.data || []);
  }, []);

  const fetchPenalties = useCallback(async () => {
    setLoading(true);
    const res = await getPenalties({ monthKey, ...(filterUserId ? { userId: filterUserId } : {}) });
    if (res.success) setPenalties(res.data || []);
    else show(res.error?.message || 'Lỗi tải danh sách', 'error');
    setLoading(false);
  }, [monthKey, filterUserId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchPenalties(); }, [fetchPenalties]);

  // Các tháng người này có dữ liệu — admin không phải nhớ tháng nào có live
  useEffect(() => {
    if (!dialogOpen || !form.userId) { setAvailableMonths([]); return; }
    getPenaltyMonths(form.userId).then(res => setAvailableMonths(res.success ? (res.data || []) : []));
  }, [dialogOpen, form.userId]);

  // Nạp GMV + thưởng đã nhận + phiếu doanh thu của TỪNG tháng vi phạm, để quyết có căn cứ
  const monthsKey = form.violationMonths.join(',');
  const chargedMonth = (form.periodDate || '').slice(0, 7);
  useEffect(() => {
    if (!dialogOpen || !form.userId || (!monthsKey && !chargedMonth)) { setContext(null); return; }
    setContextLoading(true);
    getPenaltyContext(form.userId, { months: monthsKey, chargedMonth }).then(res => {
      setContext(res.success ? res.data : null);
      setContextLoading(false);
    });
  }, [dialogOpen, form.userId, monthsKey, chargedMonth]);

  // Phiếu doanh thu của MỌI tháng đã chọn — GMV ảo có thể nằm ở bất kỳ tháng nào trong đó
  const allRevenues = useMemo(
    () => (context?.months || []).flatMap(m => m.revenues || [])
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [context]
  );

  // Tổng doanh thu các phiếu đã tick — cần thấy con số này để chọn cho đủ mức GMV ảo muốn loại
  const pickedTotal = useMemo(
    () => allRevenues.filter(r => form.revenueIds.includes(r._id))
      .reduce((s2, r) => s2 + (r.totalSales || 0), 0),
    [allRevenues, form.revenueIds]
  );

  /**
   * Mô phỏng ĐÚNG cách backend chia GMV ảo (allocateFakeGmv: trừ lần lượt cho tới hết).
   * Không có bảng này thì admin nhập 150tr mà không biết nó lấy đi bao nhiêu tiền của tháng nào.
   */
  const fakeAllocation = useMemo(() => {
    const byMonth = {};
    let remaining = form.voidSession ? Infinity : (Number(form.fakeGMV) || 0);
    const picked = allRevenues.filter(r => form.revenueIds.includes(r._id));
    for (const r of picked) {
      const mk = new Date(r.date).toISOString().slice(0, 7);
      const cut = form.voidSession ? (r.totalSales || 0) : Math.min(remaining, r.totalSales || 0);
      if (cut <= 0) continue;
      if (!form.voidSession) remaining -= cut;
      byMonth[mk] = (byMonth[mk] || 0) + cut;
    }
    const unallocated = form.voidSession ? 0 : Math.max(0, remaining);
    return { byMonth, unallocated, allocated: Object.values(byMonth).reduce((a, b) => a + b, 0) };
  }, [allRevenues, form.revenueIds, form.fakeGMV, form.voidSession]);

  /** Tự tick phiếu (mới nhất trở về trước) cho tới khi đủ mức GMV ảo — 60-70 phiếu/tháng, tick tay không nổi. */
  const autoPickRevenues = () => {
    const target = Number(form.fakeGMV) || 0;
    if (target <= 0) return show('Nhập mức GMV ảo cần loại trước', 'warning');
    const sorted = [...allRevenues].sort((a, b) => new Date(b.date) - new Date(a.date));
    const picked = [];
    let sum = 0;
    for (const r of sorted) {
      if (sum >= target) break;
      picked.push(r._id);
      sum += r.totalSales || 0;
    }
    setField('revenueIds', picked);
    show(sum >= target
      ? `Đã chọn ${picked.length} phiếu (${fmtNum(sum)}đ) đủ để loại ${fmtNum(target)}đ`
      : `Chỉ gom được ${fmtNum(sum)}đ từ ${picked.length} phiếu — chọn thêm tháng vi phạm`,
    sum >= target ? 'success' : 'warning');
  };

  /**
   * Bảng tính hiện ngay trên form, khớp cách backend tính.
   * Tách hai cột vì bản chất khác nhau: tháng đã trả lương chỉ đòi lại được bằng tiền,
   * còn kỳ đang chạy thì cắt thẳng khoản thưởng chưa trả.
   */
  const estimated = useMemo(() => {
    const pct = Number(form.gmvPenaltyPercent) || 0;
    const matches = (b) => {
      const isGmv = ['gmv_milestone', 'tiktok_gmv'].includes(b.type);
      const isNight = b.type === 'night_allowance';
      if (isGmv) return form.cutGmvBonus;
      if (isNight) return form.cutNightAllowance;
      return form.cutAttendanceBonus;
    };

    const rows = (context?.months || []).map(m => {
      const isCharged = m.monthKey === chargedMonth;
      const gmvPenalty = Math.round(m.gmv * pct / 100);
      const bonusLost = (m.bonuses || []).filter(matches).reduce((s, b) => s + b.amount, 0);
      /**
       * GMV ảo bị loại khỏi tháng này, và hai khoản THU HỒI đi kèm.
       *
       * Tháng đã chốt lương thì tiền đã trả rồi — trừ GMV khỏi phiếu doanh thu chỉ sửa lại con số
       * trên phiếu cũ, không đòi lại được đồng nào. Muốn lấy lại tiền phải thu hồi vào kỳ đang
       * chạy: hoa hồng đã trả trên phần ảo, cộng phần thưởng mốc dôi ra do GMV ảo đẩy lên.
       */
      const fakeCut = fakeAllocation.byMonth[m.monthKey] || 0;
      const claw = form.clawbackFakeGmvBenefit && fakeCut > 0 && m.hasPayslip && !isCharged;
      const commissionLost = claw
        ? Math.round(fakeCut * (m.effectiveCommissionRate || 0) / 100) : 0;
      const paidMilestone = (m.bonuses || [])
        .filter(b => b.type === 'gmv_milestone').reduce((s, b) => s + b.amount, 0);
      // Cắt sạch nhóm mốc GMV rồi thì phần chênh nằm trong đó — cộng cả hai là đòi hai lần
      const bonusDiff = (claw && !form.cutGmvBonus)
        ? Math.max(0, paidMilestone - pickGmvTier(m.gmvBonusTiers, Math.max(0, m.gmv - fakeCut)))
        : 0;
      return {
        ...m, isCharged, gmvPenalty, bonusLost, fakeCut, commissionLost, bonusDiff,
        subtotal: gmvPenalty + bonusLost + commissionLost + bonusDiff,
      };
    });

    const retro = rows.filter(r => !r.isCharged).reduce((s, r) => s + r.subtotal, 0);
    const live = rows.filter(r => r.isCharged).reduce((s, r) => s + r.subtotal, 0);
    const fixed = Number(form.fixedAmount) || 0;
    const commissionLost = rows.reduce((s, r) => s + r.commissionLost, 0);
    const bonusDiff = rows.reduce((s, r) => s + r.bonusDiff, 0);
    const fakeCut = rows.reduce((s, r) => s + r.fakeCut, 0);
    const total = retro + live + fixed;
    const n = Math.max(1, Number(form.installments) || 1);
    return {
      rows, retro, live, fixed, commissionLost, bonusDiff, fakeCut,
      total, grandLoss: total,
      perPeriod: n > 1 ? Math.floor(total / n) : total, installments: n
    };
  }, [context, chargedMonth, fakeAllocation, form.gmvPenaltyPercent, form.fixedAmount, form.installments,
    form.clawbackFakeGmvBenefit, form.cutGmvBonus, form.cutAttendanceBonus, form.cutNightAllowance]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), userId: filterUserId || '' });
    setDialogOpen(true);
  };

  const toggleMonth = (mk) => setForm(prev => ({
    ...prev,
    violationMonths: prev.violationMonths.includes(mk)
      ? prev.violationMonths.filter(x => x !== mk)
      : [...prev.violationMonths, mk].sort(),
    revenueIds: [],   // đổi tháng thì phiếu đã chọn không còn đúng nữa
  }));

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      userId: row.userId?._id || row.userId || '',
      periodDate: toInputDate(row.periodDate),
      violationMonths: row.violationMonths || [],
      installments: row.installments || 1,
      type: row.type || 'fake_order',
      description: row.description || '',
      fakeGMV: row.fakeGMV || '',
      voidSession: !!row.voidSession,
      revenueIds: (row.revenueIds || []).map(r => r._id || r),
      adjustRevenue: row.adjustRevenue !== false,
      clawbackFakeGmvBenefit: row.clawbackFakeGmvBenefit !== false,
      gmvPenaltyPercent: row.gmvPenaltyPercent || '',
      fixedAmount: row.fixedAmount || '',
      cutGmvBonus: !!row.cutGmvBonus,
      cutAttendanceBonus: !!row.cutAttendanceBonus,
      cutNightAllowance: !!row.cutNightAllowance,
    });
    setDialogOpen(true);
  };

  // GMV ảo chỉ có tác dụng khi thực sự trừ khỏi phiếu doanh thu — nhập số mà tắt công tắc thì
  // lương không đổi một đồng, dễ tưởng đã thu hồi trong khi chẳng có gì xảy ra
  const gmvIsMeasure = (Number(form.fakeGMV) > 0 || form.voidSession) && form.adjustRevenue;
  const gmvIsNoop = (Number(form.fakeGMV) > 0 || form.voidSession) && !form.adjustRevenue;
  const hasMeasure = gmvIsMeasure
    || Number(form.gmvPenaltyPercent) > 0 || Number(form.fixedAmount) > 0
    || form.cutGmvBonus || form.cutAttendanceBonus || form.cutNightAllowance;

  const handleSave = async () => {
    if (!form.userId) return show('Chọn nhân sự bị xử phạt', 'error');
    if (!form.violationMonths.length) return show('Chọn ít nhất một tháng vi phạm', 'error');
    if (!form.description.trim()) return show('Ghi căn cứ xử phạt — nhân sự sẽ đọc được nội dung này', 'error');
    if (!hasMeasure) {
      return show(gmvIsNoop
        ? 'Đã nhập GMV ảo nhưng tắt "Trừ GMV ảo khỏi phiếu doanh thu" → lương sẽ không đổi'
        : 'Chọn ít nhất một biện pháp xử phạt', 'error');
    }
    if (gmvIsMeasure && !form.revenueIds.length) {
      return show('Chọn phiếu doanh thu để trừ GMV ảo, hoặc tắt "Trừ GMV ảo khỏi phiếu doanh thu"', 'error');
    }

    setSaving(true);
    const payload = {
      ...form,
      fakeGMV: Number(form.fakeGMV) || 0,
      gmvPenaltyPercent: Number(form.gmvPenaltyPercent) || 0,
      fixedAmount: Number(form.fixedAmount) || 0,
      installments: Math.max(1, Number(form.installments) || 1),
      clawbackFakeGmvBenefit: !!form.clawbackFakeGmvBenefit,
      periodDate: new Date(form.periodDate + 'T12:00:00').toISOString(),
    };
    const res = editing ? await updatePenalty(editing._id, payload) : await createPenalty(payload);
    setSaving(false);

    if (res.success) {
      show(res.message || 'Đã lưu');
      (res.warnings || []).forEach(w => show(w, 'warning'));
      setDialogOpen(false);
      fetchPenalties();
    } else {
      show(res.error?.message || 'Lỗi lưu', 'error');
    }
  };

  const handleRevoke = async () => {
    const res = await revokePenalty(revokeTarget._id, revokeNote);
    if (res.success) {
      show(res.message);
      (res.warnings || []).forEach(w => show(w, 'warning'));
      fetchPenalties();
    } else show(res.error?.message || 'Lỗi thu hồi', 'error');
    setRevokeTarget(null);
    setRevokeNote('');
  };

  const handleDelete = async () => {
    const res = await deletePenalty(deleteTarget._id);
    if (res.success) { show(res.message); fetchPenalties(); }
    else show(res.error?.message || 'Lỗi xoá', 'error');
    setDeleteTarget(null);
  };

  const activeList = penalties.filter(p => p.status === 'active');
  const totalCash = activeList.reduce((s, p) => s + (p.appliedSnapshot?.total || 0), 0);
  const totalFakeGMV = activeList.reduce((s, p) => s + (p.fakeGMV || 0), 0);

  // Mô tả biện pháp của một quyết định, dùng chung cho bảng danh sách
  const measuresOf = (p) => [
    p.retroTotal > 0 ? `thu hồi ${fmtNum(p.retroTotal)}đ tháng cũ` : null,
    p.voidSession ? 'huỷ cả phiên' : (p.fakeGMV > 0 ? `loại ${fmtNum(p.fakeGMV)}đ GMV ảo` : null),
    p.gmvPenaltyPercent > 0 ? `trừ ${p.gmvPenaltyPercent}% GMV` : null,
    p.fixedAmount > 0 ? `phạt ${fmtNum(p.fixedAmount)}đ` : null,
    p.cutGmvBonus ? 'cắt thưởng mốc GMV' : null,
    p.cutAttendanceBonus ? 'cắt thưởng chuyên cần' : null,
    p.cutNightAllowance ? 'cắt phụ cấp ca đêm' : null,
  ].filter(Boolean);

  return (
    <Box>
      <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <GavelIcon color="error" /> Xử phạt vi phạm doanh thu
        </Typography>
        <TextField
          size="small" label="Kỳ lương" type="month" InputLabelProps={{ shrink: true }}
          value={monthKey} onChange={e => setMonthKey(e.target.value)} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" select label="Nhân sự" value={filterUserId}
          onChange={e => setFilterUserId(e.target.value)} sx={{ minWidth: 200 }}
        >
          <MenuItem value="">— Tất cả —</MenuItem>
          {liveUsers.map(u => <MenuItem key={u._id} value={u._id}>{u.fullName}</MenuItem>)}
        </TextField>
        <Tooltip title="Tải lại"><span><IconButton onClick={fetchPenalties} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
        <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', borderRadius: 2 }}>
          Ra quyết định phạt
        </Button>
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        Dùng khi bạn đã đối chiếu <b>Shopee Seller Center</b> và xác định được đơn ảo / GMV ảo.
        Bấm lưu là áp ngay: trừ GMV ảo khỏi phiếu doanh thu, tính lại phiếu lương kỳ đó, và
        gửi thông báo phạt tới nhân sự (web + app + push).
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h5" fontWeight={700} color="error.main">{activeList.length}</Typography>
              <Typography variant="caption" color="text.secondary">Quyết định đang áp trong kỳ</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} color="error.main">{fmtVnd(totalCash)}</Typography>
              <Typography variant="caption" color="text.secondary">Tiền phạt đã trừ vào lương</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700}>{fmtVnd(totalFakeGMV)}</Typography>
              <Typography variant="caption" color="text.secondary">GMV ảo đã loại khỏi doanh thu</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Nhân sự</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Hành vi</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Biện pháp đã áp</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Tiền trừ</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Trạng thái</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Thao tác</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : penalties.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">Chưa có quyết định xử phạt nào trong kỳ này</Typography>
              </TableCell></TableRow>
            ) : penalties.map(row => (
              <TableRow key={row._id} hover sx={{ opacity: row.status === 'revoked' ? 0.55 : 1 }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{row.userId?.fullName || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Vi phạm {(row.violationMonths || []).map(monthLabel).join(', ') || '—'}
                    {' · '}trừ từ kỳ {fmtDate(row.periodDate)}
                    {row.installments > 1 && ` · trả góp ${row.installments} kỳ`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={row.description || ''}>
                    <Chip size="small" variant="outlined" label={TYPE_SHORT[row.type] || row.type} />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {measuresOf(row).map(m => (
                      <Chip key={m} size="small" color="error" variant="outlined" label={m} sx={{ fontSize: '0.68rem' }} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    {fmtNum(row.appliedSnapshot?.total)}đ
                  </Typography>
                  {row.installments > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {fmtNum(row.appliedSnapshot?.perPeriod)}đ × {row.installments} kỳ
                    </Typography>
                  )}
                  {row.retroTotal > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      gồm {fmtNum(row.retroTotal)}đ thu hồi tháng cũ
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5} alignItems="flex-start">
                    <Chip size="small" color={row.status === 'active' ? 'error' : 'success'}
                      variant={row.status === 'active' ? 'filled' : 'outlined'}
                      label={row.status === 'active' ? 'Đang áp' : 'Đã thu hồi'} />
                    {row.appealedAt && (
                      <Tooltip title={row.appealNote || ''}>
                        <Chip size="small" variant="outlined" color="info" label="Có giải trình" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {row.status === 'active' && (
                      <>
                        <Tooltip title="Sửa quyết định">
                          <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Thu hồi: hoàn nguyên doanh thu, tiền phạt và thưởng bị cắt">
                          <IconButton size="small" color="success" onClick={() => setRevokeTarget(row)}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Xoá hẳn (chỉ dùng khi lập nhầm)">
                      <IconButton size="small" onClick={() => setDeleteTarget(row)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form ra quyết định */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Sửa quyết định xử phạt' : 'Ra quyết định xử phạt'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth size="small" select label="Nhân sự" value={form.userId} onChange={e => setField('userId', e.target.value)}>
                {liveUsers.map(u => <MenuItem key={u._id} value={u._id}>{u.fullName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              <Tooltip title="Kỳ lương chịu khoản trừ. Đặt về tháng cũ nếu muốn dồn hết vào một kỳ trước đó.">
                <TextField fullWidth size="small" label="Trừ vào kỳ lương của ngày" type="date" InputLabelProps={{ shrink: true }}
                  value={form.periodDate} onChange={e => setField('periodDate', e.target.value)} />
              </Tooltip>
            </Grid>

            {/* Chọn nhiều tháng: gian lận thường chỉ lộ ra sau vài kỳ, bắt admin lập từng quyết
                định cho mỗi tháng thì vừa mất công vừa dễ sót */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Các tháng vi phạm — chọn được nhiều tháng
              </Typography>
              {!form.userId ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>Chọn nhân sự trước.</Alert>
              ) : !availableMonths.length ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  Người này chưa có tháng nào phát sinh GMV.
                </Alert>
              ) : (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {availableMonths.map(m => {
                    const on = form.violationMonths.includes(m.monthKey);
                    return (
                      <Chip
                        key={m.monthKey}
                        onClick={() => toggleMonth(m.monthKey)}
                        color={on ? 'error' : 'default'}
                        variant={on ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                        label={
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" fontWeight={700}>{monthLabel(m.monthKey)}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                              GMV {fmtNum(m.gmv / 1e6)}tr{m.bonus > 0 ? ` · thưởng ${fmtNum(m.bonus / 1e6)}tr` : ''}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </Stack>
              )}
            </Grid>

            {/* Bảng từng tháng: quyết mức phạt mà không thấy GMV và thưởng đã trả thì chỉ là đoán */}
            <Grid item xs={12}>
              {contextLoading ? (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, textAlign: 'center' }}>
                  <CircularProgress size={20} />
                </Paper>
              ) : !context?.months?.length ? null : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Tháng</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">GMV</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">GMV ảo loại</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Thu hồi HH</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Thu hồi thưởng mốc</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Trừ % GMV</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Thưởng bị mất</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Cộng lại</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Cách thu</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {estimated.rows.map(r => (
                        <TableRow key={r.monthKey}>
                          <TableCell sx={{ fontWeight: 600 }}>{monthLabel(r.monthKey)}</TableCell>
                          <TableCell align="right">{fmtNum(r.gmv)}đ</TableCell>
                          <TableCell align="right">
                            {r.fakeCut > 0 ? (
                              <Tooltip title={`GMV còn lại sau khi loại: ${fmtNum(r.gmv - r.fakeCut)}đ`}>
                                <span>−{fmtNum(r.fakeCut)}đ</span>
                              </Tooltip>
                            ) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {r.commissionLost > 0 ? (
                              <Tooltip title={`Hoa hồng đã trả trên phần GMV ảo, theo % hiệu lực của kỳ (${r.effectiveCommissionRate}%)`}>
                                <span>{fmtNum(r.commissionLost)}đ</span>
                              </Tooltip>
                            ) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {r.bonusDiff > 0 ? (
                              <Tooltip title="Phần thưởng mốc GMV dôi ra do GMV ảo đẩy lên. GMV thật vẫn đạt mốc thấp hơn thì chỉ đòi phần chênh.">
                                <span>{fmtNum(r.bonusDiff)}đ</span>
                              </Tooltip>
                            ) : '—'}
                          </TableCell>
                          <TableCell align="right">{r.gmvPenalty > 0 ? `${fmtNum(r.gmvPenalty)}đ` : '—'}</TableCell>
                          <TableCell align="right">{r.bonusLost > 0 ? `${fmtNum(r.bonusLost)}đ` : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                            {fmtNum(r.subtotal)}đ
                          </TableCell>
                          <TableCell>
                            {r.isCharged ? (
                              <Chip size="small" color="warning" variant="outlined" label="cắt thẳng trên phiếu kỳ này" />
                            ) : r.hasPayslip ? (
                              <Chip size="small" color="error" variant="outlined" label="đã trả lương → thu hồi bằng tiền" />
                            ) : (
                              <Tooltip title="Tháng này chưa chốt phiếu lương nên chưa có số thưởng để thu hồi">
                                <Chip size="small" variant="outlined" label="chưa chốt lương" />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" select label="Hành vi vi phạm" value={form.type} onChange={e => setField('type', e.target.value)}>
                {PENALTY_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Căn cứ xử phạt (nhân sự sẽ đọc được)"
                value={form.description} onChange={e => setField('description', e.target.value)}
                placeholder="VD: đối chiếu Seller Center ngày 12/08 — 12 đơn từ 3 tài khoản lạ trong 20 phút cuối phiên, huỷ toàn bộ sau 2 ngày." />
            </Grid>

            <Grid item xs={12}><Divider><Chip size="small" color="error" label="Biện pháp xử phạt" /></Divider></Grid>

            {/* 1. Loại GMV ảo */}
            <Grid item xs={12} sm={5}>
              <TextField fullWidth size="small" type="number" label="GMV ảo cần loại (đ)" value={form.fakeGMV}
                disabled={form.voidSession}
                onChange={e => setField('fakeGMV', e.target.value)}
                helperText={form.fakeGMV ? fmtVnd(Number(form.fakeGMV)) : 'Bỏ trống nếu không cần loại GMV'} />
            </Grid>
            <Grid item xs={12} sm={7}>
              <Stack>
                <FormControlLabel
                  control={<Switch checked={form.voidSession} onChange={e => setField('voidSession', e.target.checked)} />}
                  label="Cả phiên là ảo (đưa doanh thu phiếu về 0)" />
                <FormControlLabel
                  control={<Switch checked={form.adjustRevenue} onChange={e => setField('adjustRevenue', e.target.checked)} />}
                  label="Trừ GMV ảo khỏi phiếu doanh thu (mất luôn hoa hồng phần đó)" />
                <Tooltip title="Tháng đã trả lương thì trừ GMV khỏi phiếu chỉ sửa lại con số trên phiếu cũ, không đòi lại được tiền. Bật cái này để thu hồi bằng tiền vào kỳ đang chạy.">
                  <FormControlLabel
                    control={<Switch checked={form.clawbackFakeGmvBenefit}
                      onChange={e => setField('clawbackFakeGmvBenefit', e.target.checked)} />}
                    label="Thu hồi hoa hồng + thưởng mốc đã trả trên GMV ảo (tháng đã chốt lương)" />
                </Tooltip>
              </Stack>
            </Grid>

            {form.adjustRevenue && (form.voidSession || Number(form.fakeGMV) > 0) && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    Chọn phiếu doanh thu chứa GMV ảo (gộp mọi tháng đã chọn), trừ lần lượt cho tới hết.
                  </Typography>
                  <Button size="small" variant="outlined" color="error" onClick={autoPickRevenues}
                    sx={{ textTransform: 'none', borderRadius: 2 }}>
                    Tự chọn cho đủ {Number(form.fakeGMV) > 0 ? fmtNum(form.fakeGMV) + 'đ' : ''}
                  </Button>
                  {form.revenueIds.length > 0 && (
                    <Chip size="small"
                      color={pickedTotal >= Number(form.fakeGMV || 0) ? 'success' : 'warning'}
                      variant="outlined"
                      label={`Đã chọn ${form.revenueIds.length} phiếu = ${fmtNum(pickedTotal)}đ`
                        + (Number(form.fakeGMV) > 0
                          ? ` / cần ${fmtNum(form.fakeGMV)}đ`
                          : '')} />
                  )}
                </Stack>
                {Number(form.fakeGMV) > 0 && form.revenueIds.length === 0 && (
                  <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                    Bạn đã nhập <b>{fmtNum(form.fakeGMV)}đ</b> GMV ảo nhưng <b>chưa chọn phiếu doanh thu nào</b> —
                    chưa có gì để trừ vào. Bấm &ldquo;Tự chọn cho đủ&rdquo; hoặc tick tay bên dưới.
                  </Alert>
                )}
                {Number(form.fakeGMV) > pickedTotal && form.revenueIds.length > 0 && (
                  <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                    Doanh thu các phiếu đã chọn ({fmtNum(pickedTotal)}đ) <b>nhỏ hơn</b> mức GMV ảo cần loại
                    ({fmtNum(form.fakeGMV)}đ) → phần thừa {fmtNum(Number(form.fakeGMV) - pickedTotal)}đ
                    sẽ không trừ được vào đâu. Chọn thêm phiếu, hoặc hạ mức GMV ảo xuống.
                  </Alert>
                )}
                {!allRevenues.length ? (
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    Không có phiếu doanh thu đã duyệt nào trong các tháng đã chọn.
                  </Alert>
                ) : (
                  <Paper variant="outlined" sx={{ borderRadius: 2, maxHeight: 220, overflow: 'auto' }}>
                    {allRevenues.map(r => {
                      const checked = form.revenueIds.includes(r._id);
                      return (
                        <Box key={r._id}
                          onClick={() => setField('revenueIds', checked
                            ? form.revenueIds.filter(id => id !== r._id)
                            : [...form.revenueIds, r._id])}
                          sx={{
                            px: 1.5, py: 0.9, cursor: 'pointer', display: 'flex', gap: 1, alignItems: 'center',
                            borderBottom: '1px solid #eee', bgcolor: checked ? '#fef2f2' : undefined,
                            '&:hover': { bgcolor: checked ? '#fee2e2' : '#f8fafc' }
                          }}>
                          <Switch size="small" checked={checked} />
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {fmtDate(r.date)} · {r.platform} · <b>{fmtVnd(r.totalSales)}</b>
                          </Typography>
                        </Box>
                      );
                    })}
                  </Paper>
                )}
              </Grid>
            )}

            {/* 2 & 3. Tiền phạt */}
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="number" label="Trừ % GMV kỳ" value={form.gmvPenaltyPercent}
                onChange={e => setField('gmvPenaltyPercent', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText={estimated.rows.some(r => r.gmvPenalty > 0)
                  ? `= ${fmtVnd(estimated.rows.reduce((s2, r) => s2 + r.gmvPenalty, 0))} (cộng cả ${estimated.rows.length} tháng)`
                  : ' '} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="number" label="Phạt tiền cố định (đ)" value={form.fixedAmount}
                onChange={e => setField('fixedAmount', e.target.value)}
                helperText={form.fixedAmount ? fmtVnd(Number(form.fixedAmount)) : ' '} />
            </Grid>

            {/* 4. Cắt thưởng */}
            <Grid item xs={12} sm={4}>
              <Stack>
                <FormControlLabel control={<Switch checked={form.cutGmvBonus} onChange={e => setField('cutGmvBonus', e.target.checked)} />}
                  label={<Typography variant="body2">Cắt thưởng mốc GMV</Typography>} />
                <FormControlLabel control={<Switch checked={form.cutAttendanceBonus} onChange={e => setField('cutAttendanceBonus', e.target.checked)} />}
                  label={<Typography variant="body2">Cắt thưởng chuyên cần</Typography>} />
                <Tooltip title="Phụ cấp ca đêm là tiền bù cho ca đã làm, không phải thưởng — cân nhắc trước khi cắt.">
                  <FormControlLabel control={<Switch checked={form.cutNightAllowance} onChange={e => setField('cutNightAllowance', e.target.checked)} />}
                    label={<Typography variant="body2">Cắt phụ cấp ca đêm</Typography>} />
                </Tooltip>
              </Stack>
            </Grid>

            {/* Trừ dồn vào một kỳ cũ: phải thấy trước phiếu lương kỳ đó còn lại bao nhiêu */}
            {context?.charged && hasMeasure && (() => {
              const c = context.charged;
              const before = c.grandTotal;
              const perPeriod = estimated.perPeriod;
              const after = before == null ? null : before - perPeriod;
              const negative = after != null && after < 0;
              return (
                <Grid item xs={12}>
                  <Alert
                    severity={negative ? 'error' : (c.isPast ? 'warning' : 'info')}
                    variant="outlined" sx={{ borderRadius: 2 }}
                  >
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                      Kỳ chịu phạt: {monthLabel(c.monthKey)}
                    </Typography>
                    {before == null ? (
                      <Typography variant="body2">
                        Kỳ này chưa chốt phiếu lương — khoản trừ sẽ vào khi hệ thống chốt kỳ.
                      </Typography>
                    ) : (
                      <Typography variant="body2">
                        Thực nhận hiện tại <b>{fmtVnd(before)}</b> → sau khi trừ {fmtVnd(perPeriod)} còn{' '}
                        <b style={{ color: negative ? '#b91c1c' : undefined }}>{fmtVnd(after)}</b>
                        {c.existingPenaltyAmount > 0 && ` (kỳ này đã có ${c.existingPenalties} quyết định khác trừ ${fmtVnd(c.existingPenaltyAmount)})`}
                      </Typography>
                    )}
                    {negative && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <b>Phiếu lương sẽ ÂM.</b> Tăng số kỳ trả góp, hoặc hạ mức phạt xuống.
                      </Typography>
                    )}
                    {c.isPast && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Kỳ này đã kết thúc{c.closedAt ? ` (chốt ${fmtDate(c.closedAt)})` : ''}. Nếu lương tháng đó
                        <b> đã trả rồi</b> thì hạ số trên phiếu cũ chỉ sửa sổ sách, không lấy lại được tiền —
                        muốn thu thật thì đặt ngày về kỳ đang chạy.
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              );
            })()}

            {/* Trả góp: thu hồi nhiều tháng dễ vượt cả tháng lương, trừ hết một lần thì phiếu về 0 */}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label="Chia làm mấy kỳ lương"
                value={form.installments}
                onChange={e => setField('installments', e.target.value)}
                inputProps={{ min: 1, max: 12 }}
                helperText={estimated.installments > 1
                  ? `${fmtVnd(estimated.perPeriod)}/kỳ trong ${estimated.installments} kỳ`
                  : 'Trừ hết trong một kỳ'} />
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: hasMeasure ? '#fef2f2' : '#f8fafc' }}>
                {gmvIsNoop && (
                  <Alert severity="error" variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                    Đã nhập GMV ảo nhưng công tắc &ldquo;Trừ GMV ảo khỏi phiếu doanh thu&rdquo; đang TẮT —
                    số này sẽ chỉ nằm trong hồ sơ, <b>lương không đổi một đồng nào</b>.
                  </Alert>
                )}
                {!hasMeasure ? (
                  <Typography variant="body2" color="text.secondary">
                    Chưa chọn biện pháp nào — quyết định phải có ít nhất một biện pháp mới lưu được.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">Nhân sự sẽ mất tổng cộng</Typography>
                    <Typography variant="h5" fontWeight={700} color="error.main">{fmtVnd(estimated.grandLoss)}</Typography>
                    {(estimated.commissionLost > 0 || estimated.bonusDiff > 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Đã gồm thu hồi {fmtVnd(estimated.commissionLost)} hoa hồng
                        {estimated.bonusDiff > 0 && ` + ${fmtVnd(estimated.bonusDiff)} thưởng mốc GMV`}
                        {' '}đã trả trên {fmtNum(estimated.fakeCut)}đ GMV ảo
                      </Typography>
                    )}
                    {estimated.fakeCut > 0 && !form.clawbackFakeGmvBenefit && (
                      <Typography variant="caption" color="error.main" sx={{ display: 'block', fontWeight: 600 }}>
                        Chưa bật thu hồi: hoa hồng và thưởng đã trả trên {fmtNum(estimated.fakeCut)}đ GMV ảo
                        sẽ KHÔNG đòi lại được (chỉ sửa số trên phiếu lương cũ).
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {estimated.retro > 0 && (
                        <Chip size="small" color="error" variant="outlined"
                          label={`Thu hồi tháng cũ ${fmtVnd(estimated.retro)}`} />
                      )}
                      {estimated.live > 0 && (
                        <Chip size="small" color="warning" variant="outlined"
                          label={`Cắt trên phiếu kỳ này ${fmtVnd(estimated.live)}`} />
                      )}
                      {estimated.fixed > 0 && (
                        <Chip size="small" color="error" variant="outlined"
                          label={`Phạt cố định ${fmtVnd(estimated.fixed)}`} />
                      )}
                      {estimated.fakeCut > 0 && (
                        <Chip size="small" color="error" variant="outlined"
                          label={`Loại ${fmtNum(estimated.fakeCut)}đ GMV ảo`} />
                      )}
                    </Stack>
                    {estimated.installments > 1 && (
                      <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                        Trừ dần <b>{fmtVnd(estimated.perPeriod)}</b>/kỳ trong <b>{estimated.installments} kỳ</b> liên tiếp,
                        kỳ cuối gánh phần lẻ.
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                Bấm lưu là áp <b>ngay lập tức</b>: sửa doanh thu, tính lại phiếu lương và gửi thông báo
                phạt cho nhân sự. Thu hồi được sau, nhưng nhân sự đã đọc thông báo.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" color="error" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (editing ? 'Lưu & báo lại' : 'Xử phạt & gửi thông báo')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thu hồi */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Thu hồi quyết định xử phạt?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Doanh thu đã trừ sẽ được hoàn nguyên, tiền phạt và các khoản thưởng bị cắt quay lại khi
            tính lại lương. Nhân sự nhận thông báo &ldquo;đã thu hồi quyết định&rdquo;.
          </DialogContentText>
          <TextField fullWidth size="small" multiline rows={2} label="Lý do thu hồi (nhân sự sẽ đọc được)"
            value={revokeNote} onChange={e => setRevokeNote(e.target.value)}
            placeholder="VD: đã kiểm tra lại Seller Center, đơn huỷ do khách đổi ý, không phải buff." />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setRevokeTarget(null)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" color="success" onClick={handleRevoke} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Thu hồi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Xoá */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Xoá hẳn quyết định?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Xoá quyết định với {deleteTarget?.userId?.fullName} và hoàn nguyên mọi thay đổi doanh thu.
            Nếu chỉ muốn huỷ hiệu lực, nên dùng &ldquo;Thu hồi&rdquo; để giữ lịch sử.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ textTransform: 'none', borderRadius: 2 }}>Xoá</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={noti.open} autoHideDuration={5000} onClose={() => setNoti(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={noti.severity} variant="filled" sx={{ borderRadius: 2 }}>{noti.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PenaltyManagement;
